import { useEffect, useMemo } from 'react'
import { Controller, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { Anchor, Title } from '@mantine/core'
import { IconBuilding, IconChevronLeft, IconLogin2 } from '@tabler/icons-react'
import { AuthLoginStep, ISSOClientFormValues } from 'auth/models'
import { setIsLoginPageUiCenter, setLoginStep } from 'auth/store'
import { generateAuthUrl } from 'auth/utils'
import { useDispatch, useForm } from 'common/hooks'
import { Button, TextInput } from 'common/lib'
import { object, string } from 'yup'

export default function SSOClientForm() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { form } = useForm<ISSOClientFormValues>({
    defaultValues: {
      clientName: '',
    },
    resolver: yupResolver(
      object({
        clientName: string().required(t('validation.required')),
      }),
    ),
  })

  const formValues = useMemo(() => form, [form])

  const handleSubmit = async (values: ISSOClientFormValues) => {
    try {
      // Generate redirect URI to /login (where OAuth provider redirects)
      const redirectUri = `${window.location.origin}/login`

      // Replace any variation of "Xelto" with "SSO" and always convert to uppercase
      const trimmedValue = values.clientName.trim()
      const clientName =
        trimmedValue.toLowerCase() === 'xelto' ? 'SSO' : trimmedValue.toUpperCase()

      // Generate OAuth URL with client name
      const authUrl = await generateAuthUrl(clientName, redirectUri)

      // Redirect to OAuth provider
      window.location.href = authUrl
    } catch (error) {
      // Reset form in case of error and stay on same page
      form.reset()
    }
  }

  const handleBackToLogin = () => {
    dispatch(setLoginStep({ step: AuthLoginStep.SIGN_IN }))
  }

  useEffect(() => {
    dispatch(setIsLoginPageUiCenter({ isLoginPageUiCenter: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={'flex flex-col justify-start w-full'}>
      <Title
        className={'font-medium mb-4 md:mb-4 md:mt-14 text-2xl text-center md:text-left'}
        order={2}
      >
        {t('auth.title.ssoLogin')}
      </Title>
      <FormProvider {...formValues}>
        <form
          className={'flex flex-col gap-6'}
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <div className={'flex flex-col gap-4'}>
            <Controller
              control={form.control}
              name={'clientName'}
              render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
                <TextInput
                  classNames={{ input: 'text-[22px]' }}
                  error={error?.message}
                  hasFilledIcon
                  label={t('auth.field.clientName')}
                  leftSection={<IconBuilding size={24} />}
                  onBlur={onBlur}
                  onChange={onChange}
                  placeholder={t('auth.placeholder.clientName')}
                  value={value}
                />
              )}
            />
          </div>
          <div className={'flex flex-col gap-4'}>
            <Anchor
              className={
                'flex items-center justify-center gap-2 font-medium no-underline focus:no-underline focus:outline-none text-[#082E9D] hover:text-[#082E9D] focus:text-[#082E9D] md:text-[#0090FE] md:hover:text-[#082E9D] md:focus:text-[#082E9D] md:justify-start'
              }
              onClick={handleBackToLogin}
              role={'button'}
              style={{ cursor: 'pointer' }}
            >
              <IconChevronLeft size={16} />
              {t('auth.control.goBack')}
            </Anchor>
            <div className={'flex justify-center md:justify-start'}>
              <Button
                leftSection={<IconLogin2 size={32} />}
                size={'large'}
                type={'submit'}
              >
                {t('auth.control.continueWithSSO')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
